import React, { useState, useEffect } from 'react';
import { Card, Form, Select, Button, Typography, Space, message, Spin } from 'antd';
import { CloseOutlined, SettingOutlined, SaveOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Text } = Typography;
const { Option } = Select;

/**
 * User Preferences Component
 * Allows users to customize their AI assistant experience
 */
const UserPreferences = ({ userId, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, [userId]);

  const loadPreferences = async () => {
    try {
      const response = await axios.get('/api/agent/preferences', {
        params: { userId },
      });

      if (response.data.success && response.data.preferences) {
        form.setFieldsValue({
          responseStyle: response.data.preferences.responseStyle || 'conversational',
          preferredPeriods: response.data.preferences.preferredPeriods || ['month'],
          roleContext: response.data.preferences.roleContext,
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const response = await axios.put('/api/agent/preferences', {
        userId,
        preferences: values,
      });

      if (response.data.success) {
        message.success('Preferences saved successfully!');
        onClose();
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      message.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      size="small"
      title={
        <Space>
          <SettingOutlined />
          <Text strong>Preferences</Text>
        </Space>
      }
      extra={
        <Button type="text" icon={<CloseOutlined />} onClick={onClose} size="small" />
      }
      style={{ marginBottom: '16px' }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          size="small"
        >
          <Form.Item
            name="responseStyle"
            label="Response Style"
            tooltip="How would you like the assistant to respond?"
          >
            <Select placeholder="Select response style">
              <Option value="concise">Concise - Brief, to-the-point answers</Option>
              <Option value="conversational">Conversational - Friendly and engaging</Option>
              <Option value="detailed">Detailed - Comprehensive explanations</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="preferredPeriods"
            label="Preferred Time Periods"
            tooltip="Default time periods for analysis"
          >
            <Select mode="multiple" placeholder="Select preferred periods">
              <Option value="day">Daily</Option>
              <Option value="week">Weekly</Option>
              <Option value="month">Monthly</Option>
              <Option value="quarter">Quarterly</Option>
              <Option value="year">Yearly</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="roleContext"
            label="Your Role"
            tooltip="Helps tailor insights to your needs"
          >
            <Select placeholder="Select your role">
              <Option value="executive">Executive</Option>
              <Option value="sales_manager">Sales Manager</Option>
              <Option value="plant_manager">Plant Manager</Option>
              <Option value="finance">Finance</Option>
              <Option value="operations">Operations</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
              >
                Save Preferences
              </Button>
              <Button onClick={onClose}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      )}
    </Card>
  );
};

export default UserPreferences;
